import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useMyCardsContext } from '#src/contexts/myCards/index.tsx';

const Root = styled.div`
  width: 100%;
  @media ${laptopAndAbove} {
    display: flex;
    gap: var(--unit);
    flex-wrap: wrap;
    width: unset;
    > * {
      width: 100%;
    }
  }
`;

const Item = styled(ButtonBase)`
  padding: var(--double-unit) var(--quadruple-unit);
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--double-unit);
  opacity: 0.3;
  transition: opacity 0.2s ease-in-out;
  border: 1px solid transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &:hover,
  &:focus {
    opacity: 0.7;
  }
  &.active {
    opacity: 1;
  }
  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit);
  }
`;

const Icon = styled.img`
  object-fit: contain;
  height: var(--double-unit);
`;

type Props = {
  cardSetSlug: string;
  path: (cardSetSlug: string) => string;
  className?: string;
};

export const CollectionSetSwitcher = ({
  cardSetSlug,
  path,
  className,
}: Props) => {
  const getCardSetConfig = useGetCardSetConfig();
  const { cardSets } = useMyCardsContext();
  const isLaptop = useIsLaptop();
  if (cardSets.length <= 1) {
    return null;
  }

  const [firstCardSet, ...otherSets] = cardSets;
  const firstCardSetConfig = getCardSetConfig(firstCardSet.slug);
  const showHighlightedSet = cardSets.length > 2;
  const scrollableSets = isLaptop && showHighlightedSet ? otherSets : cardSets;

  return (
    <Root className={className}>
      {!!isLaptop && !!showHighlightedSet && (
        <Item
          to={path(firstCardSet.slug)}
          replace
          className={classNames({
            active: firstCardSet.slug === cardSetSlug,
          })}
          style={{
            ...firstCardSetConfig.background,
            borderColor: firstCardSet.branding?.colors?.main ?? undefined,
          }}
        >
          {firstCardSetConfig.visual.brand.logoImage ? (
            <Icon src={firstCardSetConfig.visual.brand.logoImage} alt="" />
          ) : (
            <BrandXS className="text-center">{firstCardSet.name}</BrandXS>
          )}
        </Item>
      )}
      <div>
        <Scrollable
          itemToDisplay={Math.min(scrollableSets.length, isLaptop ? 3 : 2)}
          itemGap={1}
          overhang={0}
        >
          {scrollableSets.map(cardSet => {
            const { visual, background } = getCardSetConfig(cardSet.slug);
            const brandingColors = cardSet.branding?.colors;
            return (
              <Item
                key={cardSet.slug}
                to={path(cardSet.slug)}
                replace
                className={classNames({
                  active: cardSet.slug === cardSetSlug,
                })}
                style={{
                  ...background,
                  borderColor: brandingColors?.main ?? undefined,
                }}
              >
                {visual.brand.logoImage ? (
                  <Icon src={visual.brand.logoImage} alt="" />
                ) : (
                  <BrandXS className="text-center">{cardSet.name}</BrandXS>
                )}
              </Item>
            );
          })}
        </Scrollable>
      </div>
    </Root>
  );
};
